/**
 * /financeiro/custos-indiretos — Retrabalho + Atraso de terceiros (ADR-0030)
 * · redesign FIN-6.
 *
 * Fiel ao mockup Fase 5 V2.2 (#custos): KPI "Custo indireto total" + 2 blocos
 * (Retrabalho / Atraso) com fórmula editável (Horas × Custo/hora / Dias ×
 * Multa/dia) e tabela detalhada. Camada visual; o cálculo vive no backend
 * (/api/financeiro/custos-indiretos recalcula ao mudar custo_hora/multa_diaria).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiCheckSquare, FiClock } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import { formatarReais, obterCustosIndiretos } from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

// A28 — nomes que a equipe usa nos papeis (ASM/AST/ASP).
const TIPO_SOLICITACAO_LABEL = {
  ASM: "Assistência à montagem",
  AST: "Assistência técnica",
  ASP: "Assistência à produção",
};

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TIPO_ASSIST = {
  GARANTIA: "Garantia",
  DEFEITO_FABRICACAO: "Defeito de fabricação",
  DEFEITO_INSTALACAO: "Defeito de instalação",
  ERRO_PEDIDO: "Erro de pedido",
  RECLAMACAO: "Reclamação",
  DUVIDA: "Dúvida",
  OUTROS: "Outros",
};


export default function CustosIndiretos() {
  const { request } = useAuth();
  const hoje = useMemo(() => new Date(), []);
  const inicio = useMemo(() => isoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), [hoje]);
  const fim = useMemo(() => isoLocal(hoje), [hoje]);

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [custoHora, setCustoHora] = useState("120");
  const [multaDiaria, setMultaDiaria] = useState("240");

  // Refs espelham os parâmetros pra carregar() ler o valor atual sem
  // re-disparar a cada tecla (recarga só no blur/Enter).
  const custoRef = useRef(custoHora);
  const multaRef = useRef(multaDiaria);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await obterCustosIndiretos(request, {
        inicio, fim, custo_hora: custoRef.current, multa_diaria: multaRef.current,
      });
      setDados(d);
    } catch (err) {
      setError(err?.message || "Erro ao carregar custos indiretos.");
    } finally { setLoading(false); }
  }, [request, inicio, fim]);

  useEffect(() => { carregar(); }, [carregar]);

  const onCusto = (v) => { setCustoHora(v); custoRef.current = v; };
  const onMulta = (v) => { setMultaDiaria(v); multaRef.current = v; };
  const commitOnEnter = (e) => { if (e.key === "Enter") e.target.blur(); };

  const r = dados?.retrabalho || {};
  // A28: bloco irmao — retrabalho interno da equipe.
  const interno = r.interno || {};
  const a = dados?.atraso_terceiros || {};
  const breakdownR = r.breakdown_por_tipo || [];
  const breakdownA = a.breakdown_por_parceiro || [];

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Custos Indiretos</span>
          </div>
          <h1 className="page-title">Custos indiretos</h1>
          <div className="page-sub">
            Retrabalho e atraso de terceiros · {MESES[hoje.getMonth()]} {hoje.getFullYear()} · não capturados na DRE direta
          </div>
        </div>
        <div className="kpi-v2" style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "10px 16px", minWidth: 200 }}>
          <div className="lbl">Custo indireto total</div>
          <div className="val" style={{ color: "var(--danger-text)" }}>{formatarReais(dados?.custo_total_indireto)}</div>
        </div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="card-grid-2">
        {/* ===== RETRABALHO ===== */}
        <div className="block">
          <div className="block__head">
            <div className="block__title">Retrabalho (assistências)</div>
            <span className="tiny mono muted">resolvidas no mês</span>
          </div>
          <div className="block__body">
            <span className="ci-chip">
              <FiCheckSquare aria-hidden="true" />
              {r.assistencias_count || 0} assistências resolvidas · {r.horas_total || 0}h no total
            </span>
            {/* A28 (decisão do dono): retrabalho tem duas naturezas e as
                duas custam. Este bloco é o do CLIENTE (garantia/pós-venda);
                logo abaixo vem o INTERNO (auxílio à equipe de produção e
                montagem). Antes o relatório lia só este — que estava
                vazio — e mostrava R$ 0,00 com 22 chamados internos já
                atendidos. */}
            <span className="ci-hint">
              Pedidos do CLIENTE. O retrabalho interno da equipe aparece
              logo abaixo.
            </span>
            <div className="ci-formula">
              <div className="ci-cell">
                <span className="ci-k">Horas totais</span>
                <span className="ci-v">{r.horas_total || 0}h</span>
              </div>
              <span className="ci-op">×</span>
              <div className="ci-cell editable">
                <span className="ci-k">Custo/hora</span>
                <span className="ci-v">R$ <input value={custoHora} inputMode="decimal" aria-label="Custo por hora"
                  onChange={(e) => onCusto(e.target.value)} onBlur={carregar} onKeyDown={commitOnEnter} /></span>
              </div>
              <span className="ci-op">=</span>
              <div className="ci-cell total">
                <span className="ci-k">Total</span>
                <span className="ci-v">{formatarReais(r.custo_total)}</span>
              </div>
            </div>
            <table className="dtable" style={{ marginTop: 6 }}>
              <colgroup><col style={{ width: "55%" }} /><col style={{ width: "15%" }} /><col style={{ width: "15%" }} /><col className="col-money" /></colgroup>
              <thead><tr>
                <th>Tipo de assistência</th>
                <th className="num" style={{ textAlign: "right" }}>Qtd</th>
                <th className="num" style={{ textAlign: "right" }}>Horas</th>
                <th className="num" style={{ textAlign: "right" }}>Custo</th>
              </tr></thead>
              <tbody>
                {breakdownR.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 0 }}><div className="empty"><div className="msg">{loading ? "Carregando…" : "Nenhuma assistência resolvida no mês."}</div></div></td></tr>
                )}
                {breakdownR.map((row) => (
                  <tr key={row.tipo}>
                    <td className="ell" title={TIPO_ASSIST[row.tipo] || row.tipo}>{TIPO_ASSIST[row.tipo] || row.tipo}</td>
                    <td className="num">{row.quantidade}</td>
                    <td className="num">{row.horas}h</td>
                    <td className="num">{formatarReais(row.custo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* A28 — retrabalho INTERNO (Solicitações da equipe) */}
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
              <div className="block__title" style={{ marginBottom: 6 }}>
                Retrabalho interno (equipe)
              </div>
              <span className="ci-chip">
                <FiCheckSquare aria-hidden="true" />
                {interno.solicitacoes_count || 0} solicitações atendidas ·{" "}
                {interno.horas_total || 0}h no total
              </span>
              <span className="ci-hint">
                Chamados que a produção/montagem abre para si mesma — o
                conserto que nunca chegou ao cliente. A hora gasta refazendo
                não vira móvel novo.
              </span>
              <div className="ci-formula">
                <div className="ci-cell">
                  <span className="ci-k">Horas totais</span>
                  <span className="ci-v">{interno.horas_total || 0}h</span>
                </div>
                <span className="ci-op">×</span>
                <div className="ci-cell">
                  <span className="ci-k">Custo/hora</span>
                  <span className="ci-v">R$ {custoHora}</span>
                </div>
                <span className="ci-op">=</span>
                <div className="ci-cell total">
                  <span className="ci-k">Total</span>
                  <span className="ci-v">{formatarReais(interno.custo_total)}</span>
                </div>
              </div>
              {(interno.breakdown_por_tipo || []).length > 0 && (
                <table className="dtable" style={{ marginTop: 6 }}>
                  <colgroup><col style={{ width: "55%" }} /><col style={{ width: "15%" }} /><col style={{ width: "15%" }} /><col className="col-money" /></colgroup>
                  <thead><tr>
                    <th>Tipo de solicitação</th>
                    <th className="num" style={{ textAlign: "right" }}>Qtd</th>
                    <th className="num" style={{ textAlign: "right" }}>Horas</th>
                    <th className="num" style={{ textAlign: "right" }}>Custo</th>
                  </tr></thead>
                  <tbody>
                    {interno.breakdown_por_tipo.map((b) => (
                      <tr key={b.tipo}>
                        <td>{TIPO_SOLICITACAO_LABEL[b.tipo] || b.tipo}</td>
                        <td className="num" style={{ textAlign: "right" }}>{b.quantidade}</td>
                        <td className="num" style={{ textAlign: "right" }}>{b.horas}h</td>
                        <td className="num" style={{ textAlign: "right" }}>{formatarReais(b.custo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* ===== ATRASO DE TERCEIROS ===== */}
        <div className="block">
          <div className="block__head">
            <div className="block__title">Atraso de terceiros</div>
            <span className="tiny mono muted">OSEs atrasadas</span>
          </div>
          <div className="block__body">
            <span className="ci-chip">
              <FiClock aria-hidden="true" />
              {a.oses_atrasadas_count || 0} OSEs atrasadas · {a.dias_total || 0} dias somados
            </span>
            <div className="ci-formula">
              <div className="ci-cell">
                <span className="ci-k">Dias totais</span>
                <span className="ci-v">{a.dias_total || 0}</span>
              </div>
              <span className="ci-op">×</span>
              <div className="ci-cell editable">
                <span className="ci-k">Multa/dia</span>
                <span className="ci-v">R$ <input value={multaDiaria} inputMode="decimal" aria-label="Multa por dia"
                  onChange={(e) => onMulta(e.target.value)} onBlur={carregar} onKeyDown={commitOnEnter} /></span>
              </div>
              <span className="ci-op">=</span>
              <div className="ci-cell total">
                <span className="ci-k">Total</span>
                <span className="ci-v">{formatarReais(a.custo_total)}</span>
              </div>
            </div>
            {/* B17: com multa/dia zerada o total sai R$ 0,00 e passa por
                "nenhum atraso custou nada" — quando na verdade nada foi
                medido. O atraso existe (os dias estão logo ali); o que
                falta é o valor que ele custa nesta marcenaria. */}
            {Number(multaDiaria) === 0 && (a.dias_total || 0) > 0 && (
              <div className="ci-hint" style={{ color: "var(--warning-text)" }}>
                {a.dias_total} dias de atraso sem custo calculado — informe a
                multa/dia (ou o custo de oportunidade do dia parado) acima.
              </div>
            )}
            <table className="dtable" style={{ marginTop: 6 }}>
              <colgroup><col style={{ width: "55%" }} /><col style={{ width: "15%" }} /><col style={{ width: "15%" }} /><col className="col-money" /></colgroup>
              <thead><tr>
                <th>Parceiro / terceiro</th>
                <th className="num" style={{ textAlign: "right" }}>OSEs</th>
                <th className="num" style={{ textAlign: "right" }}>Dias</th>
                <th className="num" style={{ textAlign: "right" }}>Multa</th>
              </tr></thead>
              <tbody>
                {breakdownA.length === 0 && (
                  <tr><td colSpan={4} style={{ padding: 0 }}><div className="empty"><div className="msg">{loading ? "Carregando…" : "Nenhuma OSE atrasada no mês."}</div></div></td></tr>
                )}
                {breakdownA.map((row) => (
                  <tr key={row.parceiro_id || row.parceiro}>
                    <td className="ell" title={row.parceiro}>{row.parceiro}</td>
                    <td className="num">{row.oses_atrasadas}</td>
                    <td className="num">{row.dias_total}</td>
                    <td className="num">{formatarReais(row.custo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
